import { useCallback } from 'react'
import { api } from '../lib/api'
import { useAuth } from '../contexts/AuthContext'
import type { MeResponse, User } from '../lib/types'

type ConsentPatch = Partial<Pick<User, 'recommendation_consent' | 'aggregate_consent'>>

/**
 * The account-management actions for the profile page (build-contract §3.1).
 * Steam identity is read-only; only consent is editable. Every mutation reloads
 * the session so the UI reflects the server's canonical user. Thin client — no
 * recommendation logic here.
 */
export function useProfile() {
  const { refreshSession, signOut } = useAuth()

  const updateConsent = useCallback(
    async (patch: ConsentPatch) => {
      await api.patch<MeResponse>('/me', patch)
      await refreshSession()
    },
    [refreshSession],
  )

  const resyncSteam = useCallback(async () => {
    await api.post<MeResponse>('/me/steam_sync')
    await refreshSession()
  }, [refreshSession])

  // The library sync runs as a background job, so opting in doesn't make the
  // library readable synchronously. Poll /me until it flips (or we give up), so
  // the UI updates on its own instead of needing a manual reload.
  const awaitLibrarySync = useCallback(
    async ({ tries = 8, delayMs = 2000 }: { tries?: number; delayMs?: number } = {}) => {
      for (let i = 0; i < tries; i += 1) {
        const data = await api.get<MeResponse>('/me')
        if (data.user?.library_readable) break
        await new Promise((resolve) => setTimeout(resolve, delayMs))
      }
      await refreshSession()
    },
    [refreshSession],
  )

  const deleteAccount = useCallback(async () => {
    await api.delete<void>('/me')
    await signOut()
  }, [signOut])

  return { updateConsent, resyncSteam, deleteAccount, awaitLibrarySync }
}
